import classNames from 'classnames';
import { MouseEventHandler } from 'react';

import { BinMid, CheckCircleFilledMid, CheckCircleMid, InfoCircleMid } from '@zooplus/icons';
import { Tooltip } from '@zooplus/ui-components';

import CardIcon from '../CardIcon/CardIcon';
import MaskedCardNumber from '../MaskedCardNumber/MaskedCardNumber';
import styles from '../StoredCreditCards/StoredCreditCards.module.scss';

import { useTranslation } from '@hooks/useTranslations';
import { CreditCardData } from '@types-internal';
import { handleKeyDown } from '@utils/utils';

export type CreditCardProps = {
  creditCard: CreditCardData;
  onDelete?: MouseEventHandler;
  onSelect?: MouseEventHandler;
  showIcons: boolean;
  loadingCardPspIdentifier?: string;
};

/**
 * Display the tile holding all the credit card information and the buttons to interact with it.
 */
const CreditCard = ({
  creditCard,
  onDelete,
  onSelect,
  showIcons,
  loadingCardPspIdentifier,
}: CreditCardProps) => {
  const { t } = useTranslation();
  const isSessionCard = creditCard.pspIdentifier === '0';

  return (
    <div
      className={styles.creditCardContainer}
      data-card-identifier={creditCard.pspIdentifier}
      data-card-holder-name={creditCard.holderName}
      data-card-last-four-digits={creditCard.lastFourDigits}
      data-card-expiration-month={creditCard.expirationMonth}
      data-card-expiration-year={creditCard.expirationYear}
      data-zta="storedCCWrapper">
      <div
        className={classNames(
          styles.creditCard,
          creditCard.selected && styles.selected,
          creditCard.expired && styles.expired,
        )}
        onClick={onSelect}
        role="button"
        onKeyDown={(e) => handleKeyDown(e, onSelect)}
        tabIndex={0}
        data-zta={
          isSessionCard ? 'storedSessionCC' : creditCard.selected ? 'storedSelectedCC' : 'storedCC'
        }>
        <div className={styles.creditCardHeader}>
          <div>{<CardIcon type={creditCard.brand} />}</div>
          {showIcons && (onDelete || !isSessionCard) && (
            <div>
              {onDelete && (
                <span
                  className={styles.delete}
                  data-zta="removeCC"
                  onClick={onDelete}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, onDelete)}>
                  <BinMid />
                </span>
              )}
              {!isSessionCard && (
                <span data-zta="checkedCCIcon" className={styles.validity}>
                  {creditCard.selected ? <CheckCircleFilledMid /> : <CheckCircleMid />}
                </span>
              )}
            </div>
          )}
        </div>
        <div>
          <div className={styles.holderName} data-zta="storedCCHolder">
            {creditCard.holderName ?? '-'}
          </div>
          <div>
            <MaskedCardNumber
              data-zta="storedCCNumber"
              lastFourDigits={creditCard.lastFourDigits}
            />
          </div>
          <div className={styles.expiration}>
            <ExpiryDate data-zta="storedCCDate" creditCard={creditCard} />
            {creditCard.expired && (
              <>
                {' '}
                <span className={styles.expiredLabel}>
                  {creditCard.expired && t('checkout.payment.creditcard.expired.message')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {loadingCardPspIdentifier && (
        <div className={styles.loaderBackdrop}>
          {loadingCardPspIdentifier === creditCard.pspIdentifier && (
            <div className={styles.loaderLarge} />
          )}
        </div>
      )}
    </div>
  );
};

type ExpiryDateProps = {
  creditCard: CreditCardData;
  ['data-zta']?: string;
};

/**
 * The component to render the Expiry Date for a credit card.
 */
const ExpiryDate = ({ creditCard, ['data-zta']: dataZta }: ExpiryDateProps) => {
  const { t } = useTranslation();

  const { expirationMonth, expirationYear } = creditCard;

  const isExpiryMonthUnknown = !expirationMonth || expirationMonth === '**';
  const isExpiryYearUnknown = !expirationYear || expirationYear === '**';
  const isExpiryDateUnknown = isExpiryMonthUnknown || isExpiryYearUnknown;

  return (
    <span className={styles.expiryDateContainer} data-zta={dataZta}>
      {isExpiryDateUnknown ? (
        <>
          <span className={styles.creditCardDots}>
            <span className={styles.creditCardDot}>&#11044;</span>
            <span className={styles.creditCardDot}>&#11044;</span>
          </span>
          <span className={styles.separator}>/</span>
          <span className={styles.creditCardDots}>
            <span className={styles.creditCardDot}>&#11044;</span>
            <span className={styles.creditCardDot}>&#11044;</span>
          </span>
        </>
      ) : (
        <>
          <span className={styles.month}>{expirationMonth}</span>
          <span className={styles.separator}>/</span>
          <span className={styles.year}>{expirationYear}</span>
        </>
      )}

      {isExpiryDateUnknown && (
        <Tooltip
          tipId="unknown-expiry-date-tip"
          position="bottom-left"
          tipText={t('checkout.payment.creditcard.expiry_date.unknown')}>
          <InfoCircleMid aria-label="Unknown expiry date information." />
        </Tooltip>
      )}
    </span>
  );
};

export default CreditCard;
